import React from 'react';
import {
  useMedicineDetailsLazyQuery,
  MedicineDetailResult,
  MedicineDetailsQueryVariables,
  DraftQuotationDetailAddCommand,
  useAddDraftQuotationDetailMutation,
  useWholesalesQuery,
  MatchType,
} from 'features/graphql';
import { useEffect, useState, useCallback, useRef } from 'react';
import { Alert, Container, Table, Row, Col, Button } from 'react-bootstrap';
import { PaginationComponent } from 'components/Pagination';
import { graphQlErrorMessage, Paging } from 'utils/miscellaneous';
import { Loading } from 'components/Loading';
import { Plus } from 'react-bootstrap-icons';
import { useAccountContext } from 'features/auth/AccountProvider';
import { getFiscalYearStartDate, getJapaneseFiscalYear } from 'utils/dateUtils';
import { DRAFT_QUOTATIONS_PATH } from 'route/paths';
import { useNavigate } from 'react-router-dom';
import { useZoom } from 'hooks/ZoomContext';
import { buildPriceShowListStyles } from './priceShowListStyles';
import { PriceShowTableHeader } from './PriceShowTableHeader';
import { PriceShowMedicineRow } from './PriceShowMedicineRow';
import { PriceShowSearchForm, MedicineType } from './PriceShowSearchForm';
import { calculateFixedCol4Width } from './priceShowColumnWidths';
import { usePriceShowCsvExport } from './usePriceShowCsvExport';
import { PriceShowCsvConfirmModal } from './PriceShowCsvConfirmModal';

// 実際のファイルパスに合わせて修正してください。
import { AddMedicine } from '../medicine/AddMedicine';

// 日付文字列から種類と日付を抽出する関数

export const PriceShowList = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const topScrollRef = useRef<HTMLDivElement>(null);
  const bottomScrollRef = useRef<HTMLDivElement>(null);
  const accountContext = useAccountContext();
  const { zoomLevel } = useZoom();

  // 日本の年度を取得（4月1日から始まる）
  const { currentYear, previousYear } = getJapaneseFiscalYear();
  const navigate = useNavigate();

  // 施設マスタのデータから暫定/妥結のステータスを取得する（サンプル）
  // 実際の実装では、APIや親コンポーネントからデータを取得する
  // 施設マスタのデータから暫定/妥結のステータスを取得する
  const [facilityStatus, setFacilityStatus] = useState('暫定'); // デフォルト値は「暫定」

  const determineFacilityStatus = (
    conclusionDate: string | null | undefined,
    currentDate: Date,
    fiscalYear: number
  ): string => {
    if (!conclusionDate) return '暫定';

    const settlementDate = new Date(conclusionDate);
    const fiscalYearStartDate = getFiscalYearStartDate(fiscalYear);

    const isCurrentFiscalYearSettlement = settlementDate >= fiscalYearStartDate;
    if (isCurrentFiscalYearSettlement && currentDate >= settlementDate) {
      return '妥結';
    }

    return '暫定';
  };
  const [addDraftQuotationDetailMutation, { loading: addLoading }] =
    useAddDraftQuotationDetailMutation({
      onCompleted() {
        navigate(DRAFT_QUOTATIONS_PATH);
      },
    });

  useEffect(() => {
    if (accountContext.isLoggedIn()) {
      const conclusionDate = accountContext.account?.institution?.conclusionDate;
      const now = new Date();
      setFacilityStatus(determineFacilityStatus(conclusionDate, now, currentYear));
    }
  }, [accountContext, currentYear]);

  const {
    data: wholesalesData,
    loading: wholesalesLoading,
    error: wholesalesError,
  } = useWholesalesQuery();
  const MIN_COLUMN_WIDTH_REM = 3;

  const [paging, setPaging] = useState<Paging>({ page: 0, pageSize: 10 });
  const [selectedMedicines, setSelectedMedicines] = useState<MedicineDetailResult[]>([]);
  const [medicineTypes, setMedicineTypes] = useState<MedicineType[]>(['外', '注', '内']);
  const [selected] = useState<MatchType>(MatchType.Partial);
  const [excludeProcessed, setExcludeProcessed] = useState(false);
  const [medicineName, setMedicineName] = useState('');
  const [capacity, setCapacity] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [genericName, setGenericName] = useState('');
  const [janCode, setJanCode] = useState('');
  const [yjCode, setYjCode] = useState('');
  const [gs1Code, setGs1Code] = useState('');
  const [medicineCategory, setMedicineCategory] = useState('');
  const [searchOperation, setSearchOperation] = useState('and');
  const [hasPurchaseHistory, setHasPurchaseHistory] = useState(true);
  const [hasNoPurchaseHistory, setHasNoPurchaseHistory] = useState(true);

  // 固定列の基準幅を計算する関数（削除）
  // zoomLevelはCSS内で直接適用するため、この関数は不要

  const [columnWidths, setColumnWidths] = useState({
    medicineName: 20,
    startDate: 12,
    endDate: 12,
    janCode: 12,
    currentYear: 10,
    previousYear: 10,
    wholesale: 10,
    commonName: 12,
    drugCategoryName: 12,
    salesManufacturer: 12,
    capacity: 12,
    // 固定列の幅
    fixedCol1: 3,
    fixedCol2: 3,
    fixedCol3: 3.5, // アイコン列の幅を少し広げる
  });

  // 固定列の幅をピクセル単位で計算（最小幅を考慮）
  const getFixedColumnWidth = (baseWidth: number, minZoomLevel: number = 0.7) => {
    const actualWidth = baseWidth * 16 * zoomLevel;
    const minWidth = baseWidth * 16 * minZoomLevel;
    // ズームレベルが低い場合でも最小幅を保証
    return Math.ceil(Math.max(actualWidth, minWidth));
  };

  // 固定列の累積幅を計算（ピクセル単位）- 累積誤差を防ぐ
  const getFixedColumnOffsets = () => {
    // 各列の実際の幅を取得（最小幅を考慮）
    const col1Width = getFixedColumnWidth(columnWidths.fixedCol1);
    const col2Width = getFixedColumnWidth(columnWidths.fixedCol2);
    const col3Width = getFixedColumnWidth(columnWidths.fixedCol3);

    return {
      col1: 0,
      col2: col1Width,
      col3: col1Width + col2Width,
      col4: col1Width + col2Width + col3Width,
    };
  };

  const fixedOffsets = getFixedColumnOffsets();

  const [resizing, setResizing] = useState(false);
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);

  const [showAddMedicineModal, setShowAddMedicineModal] = useState(false);

  // 検索条件の折り畳み状態
  const [isSearchCollapsed, setIsSearchCollapsed] = useState(false);

  // 検索条件の折り畳み切り替え
  const toggleSearchCollapse = () => {
    setIsSearchCollapsed(!isSearchCollapsed);
  };

  // 固定列のオフセット（固定値） - 不要になったため削除
  // CSSクラスで管理するように変更済み
  const isResizableColumn = useCallback((column: string): boolean => {
    const resizableColumns = [
      'medicineName',
      'capacity',
      'commonName',
      'drugCategoryName',
      'salesManufacturer',
    ];
    return resizableColumns.includes(column);
  }, []);

  const [isListenerActive, setIsListenerActive] = useState(false);

  // 共通のスクロール幅同期関数
  const syncScrollWidth = useCallback(() => {
    if (bottomScrollRef.current && topScrollRef.current) {
      const table = bottomScrollRef.current.querySelector('table');
      const bottomContainer = bottomScrollRef.current;
      if (table && bottomContainer) {
        // コンテナの幅を取得
        const containerWidth = bottomContainer.clientWidth;

        // テーブルの実際の幅を取得（fontSize スケーリング済み）
        const tableActualWidth = table.scrollWidth;

        const needsScroll = tableActualWidth > containerWidth;

        const scrollDiv = topScrollRef.current.querySelector('div');
        const topContainer = topScrollRef.current;
        if (scrollDiv && topContainer) {
          // スクロールが必要かどうかで制御
          if (needsScroll) {
            // スクロールが必要な場合
            scrollDiv.style.width = `${tableActualWidth}px`;
            topContainer.style.display = 'block';
          } else {
            // スクロールが不要な場合
            scrollDiv.style.width = `${containerWidth}px`;
            topContainer.style.display = 'none';
          }
        }
      }
    }
  }, []);

  // スクロール同期用のuseEffect
  useEffect(() => {
    let isScrolling = false;

    const handleTopScroll = () => {
      if (!isScrolling && bottomScrollRef.current && topScrollRef.current) {
        isScrolling = true;
        bottomScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
        requestAnimationFrame(() => {
          isScrolling = false;
        });
      }
    };

    const handleBottomScroll = () => {
      if (!isScrolling && topScrollRef.current && bottomScrollRef.current) {
        isScrolling = true;
        topScrollRef.current.scrollLeft = bottomScrollRef.current.scrollLeft;
        requestAnimationFrame(() => {
          isScrolling = false;
        });
      }
    };

    const topScroll = topScrollRef.current;
    const bottomScroll = bottomScrollRef.current;

    if (topScroll) {
      topScroll.addEventListener('scroll', handleTopScroll, { passive: true });
    }
    if (bottomScroll) {
      bottomScroll.addEventListener('scroll', handleBottomScroll, { passive: true });
    }

    // 初期化とリサイズ時の同期
    syncScrollWidth();
    window.addEventListener('resize', syncScrollWidth);

    // データ更新時にも幅を再計算
    const timer = setTimeout(syncScrollWidth, 100);

    return () => {
      if (topScroll) {
        topScroll.removeEventListener('scroll', handleTopScroll);
      }
      if (bottomScroll) {
        bottomScroll.removeEventListener('scroll', handleBottomScroll);
      }
      window.removeEventListener('resize', syncScrollWidth);
      clearTimeout(timer);
    };
  }, [wholesalesData, syncScrollWidth]); // wholesalesDataが変更されたときも再同期

  useEffect(() => {
    if (!isListenerActive) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizing || !resizingColumn) return;

      const diff = (e.clientX - startX) / 16 / zoomLevel; // Convert pixels to rem considering zoom
      const newWidth = Math.max(MIN_COLUMN_WIDTH_REM, startWidth + diff);

      setColumnWidths((prev) => ({
        ...prev,
        [resizingColumn]: newWidth,
      }));
    };

    const handleMouseUp = () => {
      setResizing(false);
      setResizingColumn(null);
      setIsListenerActive(false);

      const tableContainer = containerRef.current;
      if (tableContainer) {
        tableContainer.style.cursor = '';
        tableContainer.style.userSelect = '';
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isListenerActive, resizing, resizingColumn, startX, startWidth, zoomLevel]);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent, column: string) => {
      if (!isResizableColumn(column)) return;

      e.preventDefault();
      setResizing(true);
      setResizingColumn(column);
      setStartX(e.clientX);
      setStartWidth(columnWidths[column as keyof typeof columnWidths]);
      setIsListenerActive(true);

      const tableContainer = containerRef.current;
      if (tableContainer) {
        tableContainer.style.cursor = 'col-resize';
        tableContainer.style.userSelect = 'none';
      }
    },
    [columnWidths, isResizableColumn]
  );

  const [medicineDetailsQuery, { data, loading: medicinesLoading, error }] =
    useMedicineDetailsLazyQuery();

  // CSV出力処理（D4 step6でフックへ分離）
  const { csvLoading, csvError, setCsvError, showCsvConfirm, setShowCsvConfirm, handleCsvExport } =
    usePriceShowCsvExport({
      selected,
      currentYear,
      previousYear,
      wholesales: wholesalesData?.wholesales ?? undefined,
      wholesalesError,
      facilityStatus,
    });

  // fixed-col-4列の最適な幅を計算
  useEffect(() => {
    if (data?.medicineDetails?.medicines) {
      // fixed-col-4列の幅を計算
      const fixedCol4Width = calculateFixedCol4Width(data.medicineDetails.medicines);

      // columnWidthsを更新
      setColumnWidths((prev) => {
        // 既に同じ幅の場合は更新しない
        if (prev.medicineName === fixedCol4Width) {
          return prev;
        }
        return {
          ...prev,
          medicineName: fixedCol4Width,
          capacity: fixedCol4Width,
          commonName: fixedCol4Width,
          drugCategoryName: fixedCol4Width,
          salesManufacturer: fixedCol4Width,
        };
      });
    }
  }, [data]);

  // データ更新時にテーブル幅を再同期
  useEffect(() => {
    if (data || wholesalesData) {
      const timer = setTimeout(syncScrollWidth, 100);
      return () => clearTimeout(timer);
    }
  }, [data, wholesalesData, syncScrollWidth]);

  // ズームレベル変更時にスクロール幅を再同期
  useEffect(() => {
    // ズームレベル変更後、複数回実行してレンダリング完了を確実に待つ
    const timers = [50, 150, 300, 500].map((delay) => setTimeout(syncScrollWidth, delay));
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [zoomLevel, syncScrollWidth]);

  useEffect(() => {
    handleSearch(); // handleSearchを直接呼び出す
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paging, medicineTypes, selected]);

  const onAdd = (medicine: MedicineDetailResult, on: boolean) => {
    let newSelectedMedicines: MedicineDetailResult[];
    if (on) {
      newSelectedMedicines = selectedMedicines.concat(medicine);
    } else {
      newSelectedMedicines = selectedMedicines.filter((m) => m.janCode !== medicine.janCode);
    }
    setSelectedMedicines(newSelectedMedicines);
  };

  const onTypeChange = (medicineType: MedicineType, on: boolean) => {
    let newSelectedTypes: MedicineType[];
    if (on) {
      newSelectedTypes = [...medicineTypes, medicineType];
    } else {
      newSelectedTypes = medicineTypes.filter((t) => t !== medicineType);
    }
    setMedicineTypes(newSelectedTypes);
    setPaging({ page: 0, pageSize: paging.pageSize });
  };

  const handleAddMedicines = () => {
    const commands = selectedMedicines.map((medicine) => {
      const command: DraftQuotationDetailAddCommand = {
        medicineId: medicine.medicineId,
        medicineName: medicine.medicineName ?? '',
        janCode: medicine.janCode ?? '',
        salesManufacturer: medicine.salesManufacturer ?? '',
        capacity: medicine.capacity ?? '',
        yjCode: medicine.yjCode || undefined,
        gs1Code: medicine.gs1Code || undefined,
        medicineType: medicine.medicineType || undefined,
      };
      return command;
    });
    addDraftQuotationDetailMutation({ variables: { commands } }).then((res) => {});
  };

  const handleSearch = useCallback(() => {
    const variables: MedicineDetailsQueryVariables = {
      page: Math.max(0, paging.page),
      pageSize: paging.pageSize,
      medicineTypes,
      matchType: selected,
      currentYear: currentYear,
      previousYear: previousYear,
      excludeProcessed: excludeProcessed,
      isForPriceDisplay: true,
    };

    // filterフィールドではなく、medicineNameフィールドを使用
    if (medicineName) variables.medicineName = medicineName;
    if (capacity) variables.capacity = capacity;
    if (manufacturer) variables.salesManufacturer = manufacturer;
    if (genericName) variables.commonName = genericName;
    if (janCode) variables.janCode = janCode;
    if (yjCode) variables.yjCode = yjCode;
    if (gs1Code) variables.gs1Code = gs1Code;
    if (medicineCategory) variables.drugCategoryName = medicineCategory;
    if (searchOperation) variables.searchOperation = searchOperation;
    if (excludeProcessed) variables.excludeProcessed = excludeProcessed;

    // 購入実績検索の条件設定 - 有と無のどちらもチェックされていない場合は条件を含めない
    if (hasPurchaseHistory || hasNoPurchaseHistory) {
      variables.hasPurchaseHistory = hasPurchaseHistory;
      variables.hasNoPurchaseHistory = hasNoPurchaseHistory;
    }

    medicineDetailsQuery({ variables });
  }, [
    paging.page,
    paging.pageSize,
    medicineTypes,
    selected,
    currentYear,
    previousYear,
    excludeProcessed,
    medicineName,
    capacity,
    manufacturer,
    genericName,
    janCode,
    yjCode,
    gs1Code,
    medicineCategory,
    searchOperation,
    hasPurchaseHistory,
    hasNoPurchaseHistory,
    medicineDetailsQuery,
  ]);

  // 検索ボタンクリック時のハンドラー（ページを1ページ目にリセット）
  const handleSearchButtonClick = useCallback(() => {
    // ページを1ページ目にリセットしてから検索実行
    if (paging.page !== 0) {
      setPaging({ page: 0, pageSize: paging.pageSize });
    } else {
      // 既に1ページ目の場合は直接検索実行
      handleSearch();
    }
  }, [paging.page, paging.pageSize, handleSearch]);

  const handleOpenAddMedicineModal = () => {
    setShowAddMedicineModal(true);
  };

  const handleAddMedicineOk = () => {
    setShowAddMedicineModal(false);
    handleSearch(); // 新しい医薬品が追加された可能性があるため、リストを再検索
    // AddMedicine コンポーネント内で navigate が行われるため、ここでは不要
  };

  const handleAddMedicineCancel = () => {
    setShowAddMedicineModal(false);
  };

  // 「安」マーク列の表示要否（全医薬品にわたる判定。各セルで同値のため一度だけ算出）
  const showMinPriceColumn =
    data?.medicineDetails?.medicines?.some((medicine: MedicineDetailResult) => {
      if (!medicine.deals || medicine.deals.length === 0) return false;
      let minPrice: number | null = null;
      const prices: number[] = [];
      medicine.deals.forEach((deal) => {
        const price = deal?.packagingPrice;
        if (price != null) {
          prices.push(price);
          if (minPrice === null || price < minPrice) {
            minPrice = price;
          }
        }
      });
      return minPrice !== null && prices.length > 1;
    }) ?? false;

  return (
    <>
      <Container
        ref={containerRef}
        fluid={true}
        className="mt-4 px-5 pb-3 sheet"
        style={{ marginBottom: '4rem' }}
      >
        <style>
          {buildPriceShowListStyles(zoomLevel, columnWidths, fixedOffsets, getFixedColumnWidth)}
        </style>
        <Row></Row>
        <Row className="mt-4">
          <Col>
            <div className="fs-3">
              価格表 {currentYear}年度{' '}
              <span className={facilityStatus === '暫定' ? 'text-danger' : 'text-success'}>
                {facilityStatus}
              </span>{' '}
              期間中
              {facilityStatus === '暫定' && (
                <span className="ms-3 fs-6">※前年度価格を参考価格として掲載しています</span>
              )}
            </div>
          </Col>
          <Col xs="auto" className="d-flex align-items-center gap-3">
            {accountContext.account?.contractor?.discountRate !== null &&
              accountContext.account?.contractor?.discountRate !== undefined && (
                <div className="fs-5">
                  加重平均値引率
                  {accountContext.account?.contractor?.discountRateSpan &&
                    `（${accountContext.account.contractor.discountRateSpan}）`}
                  : {accountContext.account.contractor.discountRate}%
                </div>
              )}
            <Button
              variant="light"
              className="d-flex align-items-center gap-2 rounded-pill px-4 border"
              onClick={handleOpenAddMedicineModal}
            >
              <Plus size={18} />
              <span>検索結果にない場合はこちら</span>
            </Button>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col>
            <PriceShowSearchForm
              isSearchCollapsed={isSearchCollapsed}
              toggleSearchCollapse={toggleSearchCollapse}
              medicineName={medicineName}
              setMedicineName={setMedicineName}
              capacity={capacity}
              setCapacity={setCapacity}
              manufacturer={manufacturer}
              setManufacturer={setManufacturer}
              genericName={genericName}
              setGenericName={setGenericName}
              janCode={janCode}
              setJanCode={setJanCode}
              yjCode={yjCode}
              setYjCode={setYjCode}
              gs1Code={gs1Code}
              setGs1Code={setGs1Code}
              medicineCategory={medicineCategory}
              setMedicineCategory={setMedicineCategory}
              setSearchOperation={setSearchOperation}
              paging={paging}
              setPaging={setPaging}
              handleSearchButtonClick={handleSearchButtonClick}
              medicineTypes={medicineTypes}
              onTypeChange={onTypeChange}
              excludeProcessed={excludeProcessed}
              setExcludeProcessed={setExcludeProcessed}
              hasPurchaseHistory={hasPurchaseHistory}
              setHasPurchaseHistory={setHasPurchaseHistory}
              hasNoPurchaseHistory={hasNoPurchaseHistory}
              setHasNoPurchaseHistory={setHasNoPurchaseHistory}
            />
          </Col>
        </Row>

        <Row className="mt-3">
          <Col className="d-flex justify-content-end">
            <Button
              variant="outline-success"
              className="rounded-pill"
              onClick={() => {
                setCsvError(null);
                setShowCsvConfirm(true);
              }}
              disabled={csvLoading || wholesalesLoading}
            >
              {csvLoading ? 'CSV出力中…' : 'CSV出力'}
            </Button>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col>
            {error && <Alert variant="danger">{graphQlErrorMessage(error)}</Alert>}
            {wholesalesError && (
              <Alert variant="danger">
                卸情報の取得に失敗しました。画面を再読み込みのうえ、再度お試しください。
              </Alert>
            )}
            {csvError && <Alert variant="danger">{csvError}</Alert>}
          </Col>
        </Row>

        <Row>
          {/* 上部スクロールバー */}
          <div ref={topScrollRef} className="top-scrollbar-container">
            <div style={{ width: '1px', height: '1px', minWidth: '100%' }}></div>
          </div>

          <div
            ref={bottomScrollRef}
            style={{
              overflowY: 'auto',
              overflowX: 'auto',
              padding: '0px 0px 12px 0px',
              maxHeight: '80vh',
              width: '100%',
              fontSize: `${zoomLevel}em`,
              position: 'relative',
            }}
          >
            <Table
              className="table-borderless my-table"
              style={{
                width: 'max-content',
                minWidth: '100%',
                tableLayout: 'auto',
              }}
            >
              <PriceShowTableHeader
                zoomLevel={zoomLevel}
                handleResizeStart={handleResizeStart}
                wholesales={wholesalesData?.wholesales ?? []}
                pricelistUpdate={
                  (accountContext.account?.notice.pricelistUpdate as unknown as string) ?? null
                }
                showMinPriceColumn={showMinPriceColumn}
              />

              {data?.medicineDetails?.medicines?.map((medicine, index) => (
                <PriceShowMedicineRow
                  key={medicine.janCode || index}
                  medicine={medicine}
                  wholesales={wholesalesData?.wholesales ?? []}
                  showMinPriceColumn={showMinPriceColumn}
                  zoomLevel={zoomLevel}
                  currentYear={currentYear}
                  previousYear={previousYear}
                  facilityStatus={facilityStatus}
                  fixedCol3Width={columnWidths.fixedCol3}
                  onAdd={onAdd}
                />
              ))}
              <tfoot className="border-top border-success border-3">
                <tr></tr>
              </tfoot>
            </Table>
          </div>
          <PaginationComponent
            total={data?.medicineDetails?.count ?? 0}
            itemsPerPage={paging.pageSize}
            currentPage={paging.page + 1}
            pageSize={paging.pageSize}
            onPageChange={(page) => {
              setPaging({ page: page - 1, pageSize: paging.pageSize });
              setSelectedMedicines([]);
            }}
            onPageSize={(pageSize) => {
              setPaging({ page: 0, pageSize: +pageSize });
              setSelectedMedicines([]);
            }}
          >
            <Button
              variant="success"
              className="btn-light-gray rounded-pill py-2 px-4 me-3"
              disabled={selectedMedicines.length === 0}
              onClick={handleAddMedicines}
            >
              チェックした{selectedMedicines.length} 件を見積に追加
            </Button>
          </PaginationComponent>
        </Row>
      </Container>
      {medicinesLoading && <Loading />}
      <AddMedicine
        show={showAddMedicineModal}
        handleOk={handleAddMedicineOk}
        handleCancel={handleAddMedicineCancel}
      />
      <PriceShowCsvConfirmModal
        show={showCsvConfirm}
        onCancel={() => setShowCsvConfirm(false)}
        onOk={() => {
          setShowCsvConfirm(false);
          handleCsvExport();
        }}
      />
      {(medicinesLoading || addLoading || csvLoading) && <Loading />}{' '}
      {/* 既存のLoading表示にaddLoadingも追加 */}
    </>
  );
};

export default PriceShowList;
